import { type FC, useEffect, useRef } from 'react';

import { DashboardEvent } from 'features/dashboard/lib/constants';
import { ModalType } from 'shared/lib/analytics/modalTypes';
import { trackEvent } from 'shared/lib/analytics/trackEvent';

import { UpgradePromoEvent } from '../lib/constants';
import { usePromoStore } from '../lib/usePromoStore';
import { useUpgradePromoActions } from '../lib/useUpgradePromoActions';
import { useUpgradeTrigger } from '../lib/useUpgradeTrigger';
import { UpgradePromoModal } from './UpgradePromoModal';
import { UpgradePromoToast } from './UpgradePromoToast';

/**
 * Mount-once orchestrator for the upgrade-plan promo (CL-6252). Owns the
 * modal and toast variants only — the banner variant is rendered by
 * `layouts/2Pane` so it participates in the global single-banner precedence
 * chain (see `useUpgradePromoBannerProps`). Banner SHOWN analytics fires
 * from the banner component itself so it only counts actual paints.
 */
export const UpgradePromo: FC = () => {
  const decision = useUpgradeTrigger();
  const actions = useUpgradePromoActions(decision);
  const { recordVariantBucket, variantBucket } = usePromoStore();
  const shownRef = useRef(false);

  const reason = decision.eligible ? null : decision.reason;

  useEffect(() => {
    if (reason) {
      trackEvent(UpgradePromoEvent.ELIGIBILITY_EVALUATED, { reason });
    }
  }, [reason]);

  useEffect(() => {
    if (decision.eligible) {
      if (!variantBucket && (decision.layout === 'modal' || decision.layout === 'toast')) {
        recordVariantBucket(decision.layout);
      }
      if (!shownRef.current && decision.layout !== 'banner') {
        shownRef.current = true;
        trackEvent(UpgradePromoEvent.SHOWN, {
          layout: decision.layout,
          target_tier: decision.pitch.targetTier,
        });
        if (decision.layout === 'modal') {
          trackEvent(DashboardEvent.POPUP_SHOW, { type: ModalType.UPGRADE_PROMO });
        }
      }
    } else {
      shownRef.current = false;
    }
  }, [decision, recordVariantBucket, variantBucket]);

  if (!decision.eligible || decision.layout === 'banner' || !actions) return null;

  if (decision.layout === 'toast') {
    return (
      <UpgradePromoToast
        pitch={decision.pitch}
        onCta={actions.onCta}
        onMaybeLater={actions.onMaybeLater}
        onDismiss={actions.onDismiss}
      />
    );
  }
  return (
    <UpgradePromoModal
      pitch={decision.pitch}
      onCta={actions.onCta}
      onMaybeLater={actions.onMaybeLater}
      onDismiss={actions.onDismiss}
      onViewAllPlans={actions.onViewAllPlans}
    />
  );
};
